import { useEffect, useState } from 'react'
import { Table, Space, Button, Pagination, Input, Col, Row, Select, Tag, Divider } from 'antd'
import { isEmpty, size, trim } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../hooks'
import { PAGE_SIZE, getPage } from '../../helpers/constants'
import { Loading } from '../../common'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import useModal from 'src/helpers/modal/useModal'
import useWindowDimensions from '../../common/useWindowDimensions'
import CreateWhitelist from './CreateWhitelist'
import EditWhitelist from './EditWhitelist'

const WhitelistManagement = observer((props: any) => {
   const WhitelistStore = useStore('WhitelistStore')
   const [textSearch, setTextSearch] = useState('')
   const [isLoading, setIsLoading] = useState(false)
   const { height } = useWindowDimensions()
   const [visibleCreate, setVisibleCreate] = useState(false)
   const [visibleEdit, setVisibleEdit] = useState(false)
   const [item, setItem] = useState<any>(null)
   const [groupFilter, setGroupFilter] = useState<string>('')
   const { confirm } = useModal()

   const { rows, offset, count } = WhitelistStore.listWhitelist

   useEffect(() => {
      getListData(0)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [textSearch, groupFilter])

   const getListData = async (currentPage: number) => {
      try {
         setIsLoading(true)
         const page = currentPage + 1
         const params: any = { page }
         if (size(trim(textSearch)) > 0) {
            params.ip = trim(textSearch)
         }
         if (groupFilter) {
            params.group = groupFilter
         }
         await WhitelistStore.getWhitelist(params)
      } catch (error) {
         // console.log(error)
      } finally {
         setIsLoading(false)
      }
   }

   const onChange = (currentPage: number) => {
      getListData(currentPage - 1)
   }

   const handleChangeTextSearch = (value: any) => {
      setTextSearch(value)
   }

   const handleChangeGroup = (value: any) => {
      setGroupFilter(value || '')
   }

   const showEdit = (record: any) => {
      setItem(JSON.parse(JSON.stringify(record)))
      setTimeout(() => {
         setVisibleEdit(true)
      }, 200)
   }

   const handleEdit = () => {
      setVisibleEdit(false)
      setItem(null)
      getListData(0)
   }

   const handleDelete = (record: any) => {
      confirm({
         title: 'Bạn muốn xóa bản ghi này?',
         icon: <ExclamationCircleOutlined />,
         okText: 'Đồng ý',
         cancelText: 'Hủy',
         onCancel() {},
         async onOk() {
            await WhitelistStore.deleteWhitelist(record?.id)
            getListData(0)
         }
      })
   }

   const handleCreate = () => {
      setVisibleCreate(false)
      getListData(0)
   }

   const columns = [
      {
         title: 'ID',
         dataIndex: 'id',
         key: 'id',
         align: 'center' as 'center',
         width: 60
      },
      {
         title: 'IP',
         dataIndex: 'ip',
         key: 'ip',
         width: 150,
         render: (value: any) => <span className="txtMax2Line">{value}</span>
      },
      {
         title: 'Nhóm',
         dataIndex: 'group',
         key: 'group',
         width: 100,
         render: (value: string) => {
            const colorMap: any = {
               delivery: 'blue',
               webhook: 'green',
               order: 'orange'
            }
            return <Tag color={colorMap[value] || 'default'}>{value}</Tag>
         }
      },
      {
         title: 'Username',
         dataIndex: 'username',
         key: 'username',
         width: 120,
         render: (value: any) => <span className="txtMax2Line">{value || '-'}</span>
      },
      {
         title: 'Ghi chú',
         dataIndex: 'note',
         key: 'note',
         width: 200,
         render: (value: any) => <span className="txtMax2Line">{value || '-'}</span>
      },
      {
         title: 'Trạng thái',
         dataIndex: 'isActive',
         key: 'isActive',
         width: 100,
         align: 'center' as 'center',
         render: (value: boolean) => <Tag color={value ? 'green' : 'red'}>{value ? 'Hoạt động' : 'Tắt'}</Tag>
      },
      {
         title: 'Action',
         key: 'action',
         width: 120,
         align: 'center' as 'center',
         fixed: 'right' as 'right',
         render: (record: any) => (
            <span>
               <span onClick={() => showEdit(record)} className="gx-link">
                  Sửa
               </span>
               <Divider type="vertical" />
               <span onClick={() => handleDelete(record)} className="gx-link">
                  Xóa
               </span>
            </span>
         )
      }
   ]

   const renderPagination = () => {
      if (isEmpty(rows)) {
         return null
      }

      return (
         <div className="containerPagination">
            <Pagination
               onChange={onChange}
               pageSize={PAGE_SIZE}
               current={offset / PAGE_SIZE + 1}
               showSizeChanger={false}
               total={count}
            />
         </div>
      )
   }

   return (
      <>
         <Loading isLoading={isLoading} />
         <Row>
            <Col span={24}>
               <Row className="row-container-head">
                  <Col xs={24} md={8}>
                     <Input.Search onSearch={handleChangeTextSearch} placeholder="Tìm kiếm theo IP" allowClear />
                  </Col>
                  <Col xs={24} md={4}>
                     <Select
                        value={groupFilter || undefined}
                        placeholder="Chọn nhóm"
                        onChange={handleChangeGroup}
                        allowClear
                        style={{ minWidth: 120 }}
                     >
                        <Select.Option value="delivery">Delivery</Select.Option>
                        <Select.Option value="webhook">Webhook</Select.Option>
                        <Select.Option value="order">Order</Select.Option>
                     </Select>
                  </Col>
                  <Col xs={24} md={12}>
                     <div className="text-right">
                        <Space>
                           <Button type="primary" className="btn-add" onClick={() => setVisibleCreate(true)}>
                              <i className="icon icon-add icon-in-button" />
                              Thêm mới
                           </Button>
                        </Space>
                     </div>
                  </Col>
               </Row>

               <Space
                  className="total-items"
                  style={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}
               >
                  <span>Tổng: {count}</span>
               </Space>

               <Table
                  scroll={{ x: 900, y: height - 280 }}
                  columns={columns}
                  dataSource={rows}
                  rowKey="id"
                  pagination={false}
               />

               {renderPagination()}
            </Col>
         </Row>
         {visibleCreate && (
            <CreateWhitelist visible={visibleCreate} onOk={handleCreate} onCancel={() => setVisibleCreate(false)} />
         )}
         {visibleEdit && item && (
            <EditWhitelist
               visible={visibleEdit}
               item={item}
               onOk={handleEdit}
               onCancel={() => {
                  setVisibleEdit(false)
                  setItem(null)
               }}
            />
         )}
      </>
   )
})

export default WhitelistManagement
